import * as Y from 'yjs';
import { 
  Household, 
  Member, 
  Account, 
  Category, 
  Transaction, 
  BudgetAllocation,
  Schedule,
  UUID 
} from '../types';

// Yjs document structure for the budget ledger
export class LedgerDocument {
  private ydoc: Y.Doc;
  
  // Root level maps and arrays
  private household: Y.Map<any>;
  private members: Y.Array<any>;
  private accounts: Y.Array<any>;
  private categories: Y.Array<any>;
  private transactions: Y.Array<any>;
  private budgetAllocations: Y.Array<any>;
  private schedules: Y.Array<any>; // Recurring transactions
  private goals: Y.Array<any>; // Savings goals
  private rules: Y.Array<any>; // Automation rules
  private audit: Y.Array<any>; // Audit log

  constructor(existingDoc?: Y.Doc) {
    this.ydoc = existingDoc || new Y.Doc();
    
    // Initialize root structures
    this.household = this.ydoc.getMap('household');
    this.members = this.ydoc.getArray('members');
    this.accounts = this.ydoc.getArray('accounts');
    this.categories = this.ydoc.getArray('categories');
    this.transactions = this.ydoc.getArray('transactions');
    this.budgetAllocations = this.ydoc.getArray('budgetAllocations');
    this.schedules = this.ydoc.getArray('schedules');
    this.goals = this.ydoc.getArray('goals');
    this.rules = this.ydoc.getArray('rules');
    this.audit = this.ydoc.getArray('audit');
  }

  // Get the underlying Yjs document
  getYDoc(): Y.Doc {
    return this.ydoc;
  }

  // Initialize a new ledger with initial data
  initializeNewLedger(householdData: Household, ownerMember: Member): void {
    // Set household info
    this.household.set('id', householdData.id);
    this.household.set('name', householdData.name);
    this.household.set('createdAt', new Date().toISOString());
    this.household.set('version', 1);

    // Add owner as first member
    this.members.push([this.memberToYMap(ownerMember)]);

    // Add default categories - comprehensive taxonomy
    const defaultCategories: Omit<Category, 'id'>[] = [
      // Housing
      { name: 'Rent / Mortgage', group: 'Housing', envelope: false, rollover: 'none', target: 0 },
      { name: 'Property Taxes', group: 'Housing', envelope: false, rollover: 'none', target: 0 },
      { name: 'Home Insurance', group: 'Housing', envelope: false, rollover: 'none', target: 0 },
      { name: 'Repairs & Maintenance', group: 'Housing', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Utilities – Electricity', group: 'Housing', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Utilities – Water', group: 'Housing', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Utilities – Gas/Heating', group: 'Housing', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Internet & Cable', group: 'Housing', envelope: false, rollover: 'none', target: 0 },
      { name: 'Phone/Mobile', group: 'Housing', envelope: false, rollover: 'none', target: 0 },

      // Food
      { name: 'Groceries', group: 'Food', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Restaurants / Dining Out', group: 'Food', envelope: true, rollover: 'none', target: 0 },
      { name: 'Coffee / Snacks', group: 'Food', envelope: true, rollover: 'none', target: 0 },
      { name: 'Alcohol & Bars', group: 'Food', envelope: true, rollover: 'none', target: 0 },

      // Transportation
      { name: 'Gas / Fuel', group: 'Transportation', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Public Transit', group: 'Transportation', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Ride Sharing / Taxi', group: 'Transportation', envelope: true, rollover: 'none', target: 0 },
      { name: 'Vehicle Insurance', group: 'Transportation', envelope: false, rollover: 'none', target: 0 },
      { name: 'Vehicle Loan/Lease', group: 'Transportation', envelope: false, rollover: 'none', target: 0 },
      { name: 'Vehicle Maintenance & Repairs', group: 'Transportation', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Parking & Tolls', group: 'Transportation', envelope: true, rollover: 'none', target: 0 },

      // Health & Medical
      { name: 'Health Insurance', group: 'Health & Medical', envelope: false, rollover: 'none', target: 0 },
      { name: 'Doctor Visits', group: 'Health & Medical', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Dental', group: 'Health & Medical', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Vision', group: 'Health & Medical', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Prescriptions / Pharmacy', group: 'Health & Medical', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Gym & Fitness', group: 'Health & Medical', envelope: true, rollover: 'none', target: 0 },

      // Debt & Savings
      { name: 'Credit Card Payments', group: 'Debt & Savings', envelope: false, rollover: 'none', target: 0 },
      { name: 'Loan Payments (Student/Personal)', group: 'Debt & Savings', envelope: false, rollover: 'none', target: 0 },
      { name: 'Emergency Fund', group: 'Debt & Savings', envelope: false, rollover: 'carry', target: 0 },
      { name: 'Retirement Contributions', group: 'Debt & Savings', envelope: false, rollover: 'none', target: 0 },
      { name: 'Investments', group: 'Debt & Savings', envelope: false, rollover: 'none', target: 0 },

      // Lifestyle & Personal
      { name: 'Clothing', group: 'Lifestyle & Personal', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Entertainment (Movies, Concerts, Streaming)', group: 'Lifestyle & Personal', envelope: true, rollover: 'cap', target: 100 },
      { name: 'Hobbies', group: 'Lifestyle & Personal', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Travel & Vacations', group: 'Lifestyle & Personal', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Gifts & Donations', group: 'Lifestyle & Personal', envelope: true, rollover: 'none', target: 0 },
      { name: 'Subscriptions & Memberships', group: 'Lifestyle & Personal', envelope: false, rollover: 'none', target: 0 },

      // Family & Education
      { name: 'Childcare', group: 'Family & Education', envelope: false, rollover: 'none', target: 0 },
      { name: 'School Tuition', group: 'Family & Education', envelope: false, rollover: 'none', target: 0 },
      { name: 'School Supplies', group: 'Family & Education', envelope: true, rollover: 'carry', target: 0 },
      { name: 'Extracurricular Activities', group: 'Family & Education', envelope: true, rollover: 'none', target: 0 },
      { name: 'Pets (Food, Vet, Supplies)', group: 'Family & Education', envelope: true, rollover: 'carry', target: 0 },

      // Miscellaneous
      { name: 'Bank Fees', group: 'Miscellaneous', envelope: true, rollover: 'none', target: 0 },
      { name: 'Postage / Shipping', group: 'Miscellaneous', envelope: true, rollover: 'none', target: 0 },
      { name: 'Miscellaneous / Other', group: 'Miscellaneous', envelope: true, rollover: 'none', target: 0 },
    ];

    defaultCategories.forEach(category => {
      this.addCategory({ ...category, id: this.generateId() });
    });

    this.logAuditEvent('ledger_created', ownerMember.id, { household: householdData.name });
  }

  // Household operations
  getHousehold(): Household | null {
    const id = this.household.get('id');
    const name = this.household.get('name');
    
    if (!id || !name) return null;
    
    return {
      id,
      name,
      members: this.getMembers()
    };
  }

  updateHouseholdName(name: string, userId: UUID): void {
    this.household.set('name', name);
    this.household.set('updatedAt', new Date().toISOString());
    this.logAuditEvent('household_renamed', userId, { newName: name });
  }

  // Member operations
  getMembers(): Member[] {
    return this.members.toArray().map(this.yMapToMember);
  }

  addMember(member: Member, addedBy: UUID): void {
    this.members.push([this.memberToYMap(member)]);
    this.logAuditEvent('member_added', addedBy, { member: member.display, role: member.role });
  }

  updateMemberRole(memberId: UUID, newRole: Member['role'], updatedBy: UUID): void {
    const memberIndex = this.members.toArray().findIndex(m => m.get('id') === memberId);
    if (memberIndex !== -1) {
      const member = this.members.get(memberIndex);
      member.set('role', newRole);
      this.logAuditEvent('member_role_changed', updatedBy, { memberId, newRole });
    }
  }

  // Account operations
  getAccounts(): Account[] {
    return this.accounts.toArray().map(this.yMapToAccount);
  }

  addAccount(account: Account, addedBy: UUID): void {
    const yAccount = this.accountToYMap(account);
    yAccount.set('createdAt', new Date().toISOString());
    yAccount.set('createdBy', addedBy);
    
    this.accounts.push([yAccount]);
    this.logAuditEvent('account_added', addedBy, { account: account.name, type: account.type });
  }

  updateAccount(accountId: UUID, updates: Partial<Account>, updatedBy: UUID): void {
    const accountIndex = this.accounts.toArray().findIndex(a => a.get('id') === accountId);
    if (accountIndex !== -1) {
      const account = this.accounts.get(accountIndex);
      Object.entries(updates).forEach(([key, value]) => {
        if (key !== 'id') {
          account.set(key, value);
        }
      });
      account.set('updatedAt', new Date().toISOString());
      this.logAuditEvent('account_updated', updatedBy, { accountId, updates: Object.keys(updates) });
    }
  }

  // Category operations
  getCategories(): Category[] {
    return this.categories.toArray().map(this.yMapToCategory);
  }

  addCategory(category: Category, addedBy?: UUID): void {
    const yCategory = this.categoryToYMap(category);
    if (addedBy) {
      yCategory.set('createdAt', new Date().toISOString());
      yCategory.set('createdBy', addedBy);
    }
    
    this.categories.push([yCategory]);
    if (addedBy) {
      this.logAuditEvent('category_added', addedBy, { category: category.name, group: category.group });
    }
  }

  updateCategory(categoryId: UUID, updates: Partial<Category>, updatedBy: UUID): void {
    const categoryIndex = this.categories.toArray().findIndex(c => c.get('id') === categoryId);
    if (categoryIndex !== -1) {
      const category = this.categories.get(categoryIndex);
      Object.entries(updates).forEach(([key, value]) => {
        if (key !== 'id') {
          category.set(key, value);
        }
      });
      category.set('updatedAt', new Date().toISOString());
      this.logAuditEvent('category_updated', updatedBy, { categoryId, updates: Object.keys(updates) });
    }
  }

  // Transaction operations
  getTransactions(): Transaction[] {
    return this.transactions.toArray().map(this.yMapToTransaction);
  }

  addTransaction(transaction: Transaction): void {
    const yTransaction = this.transactionToYMap(transaction);
    this.transactions.push([yTransaction]);
    this.logAuditEvent('transaction_added', transaction.createdBy, { 
      amount: transaction.amount, 
      payee: transaction.payee,
      account: transaction.accountId 
    });
  }

  updateTransaction(transactionId: UUID, updates: Partial<Transaction>, updatedBy: UUID): void {
    const transactionIndex = this.transactions.toArray().findIndex(t => t.get('id') === transactionId);
    if (transactionIndex !== -1) {
      const transaction = this.transactions.get(transactionIndex);
      Object.entries(updates).forEach(([key, value]) => {
        if (key !== 'id') {
          transaction.set(key, value);
        }
      });
      transaction.set('updatedAt', new Date().toISOString());
      this.logAuditEvent('transaction_updated', updatedBy, { transactionId });
    }
  }

  deleteTransaction(transactionId: UUID, deletedBy: UUID): void {
    const transactionIndex = this.transactions.toArray().findIndex(t => t.get('id') === transactionId);
    if (transactionIndex !== -1) {
      this.transactions.delete(transactionIndex, 1);
      this.logAuditEvent('transaction_deleted', deletedBy, { transactionId });
    }
  }

  // Budget allocation operations
  getBudgetAllocations(): BudgetAllocation[] {
    return this.budgetAllocations.toArray().map(this.yMapToBudgetAllocation);
  }

  updateBudgetAllocation(categoryId: UUID, month: string, allocated: number, updatedBy: UUID): void {
    const existingIndex = this.budgetAllocations.toArray().findIndex(
      b => b.get('categoryId') === categoryId && b.get('month') === month
    );

    if (existingIndex !== -1) {
      const allocation = this.budgetAllocations.get(existingIndex);
      allocation.set('allocated', allocated);
      allocation.set('updatedAt', new Date().toISOString());
    } else {
      const newAllocation = new Y.Map();
      newAllocation.set('categoryId', categoryId);
      newAllocation.set('month', month);
      newAllocation.set('allocated', allocated);
      newAllocation.set('spent', 0);
      newAllocation.set('rollover', 0);
      newAllocation.set('createdAt', new Date().toISOString());
      newAllocation.set('updatedBy', updatedBy);
      
      this.budgetAllocations.push([newAllocation]);
    }

    this.logAuditEvent('budget_allocation_updated', updatedBy, { categoryId, month, allocated });
  }

  // Schedule operations
  getSchedules(): Schedule[] {
    return this.schedules.toArray().map(this.yMapToSchedule);
  }

  addSchedule(schedule: Schedule, addedBy: UUID): void {
    const ySchedule = this.scheduleToYMap(schedule);
    ySchedule.set('createdAt', new Date().toISOString());
    ySchedule.set('createdBy', addedBy);
    
    this.schedules.push([ySchedule]);
    this.logAuditEvent('schedule_added', addedBy, { 
      type: schedule.type, 
      spec: schedule.spec, 
      amount: schedule.template.amount 
    });
  }

  updateSchedule(scheduleId: UUID, updates: Partial<Schedule>, updatedBy: UUID): void {
    const scheduleIndex = this.schedules.toArray().findIndex(s => s.get('id') === scheduleId);
    if (scheduleIndex !== -1) {
      const schedule = this.schedules.get(scheduleIndex);
      
      Object.entries(updates).forEach(([key, value]) => {
        if (key !== 'id') {
          if (key === 'template' && typeof value === 'object') {
            // Handle nested template object
            const templateMap = schedule.get('template') || new Y.Map();
            Object.entries(value).forEach(([tKey, tValue]) => {
              templateMap.set(tKey, tValue);
            });
            schedule.set('template', templateMap);
          } else {
            schedule.set(key, value);
          }
        }
      });
      
      schedule.set('updatedAt', new Date().toISOString());
      this.logAuditEvent('schedule_updated', updatedBy, { scheduleId, updates: Object.keys(updates) });
    }
  }

  deleteSchedule(scheduleId: UUID, deletedBy: UUID): void {
    const scheduleIndex = this.schedules.toArray().findIndex(s => s.get('id') === scheduleId);
    if (scheduleIndex !== -1) {
      this.schedules.delete(scheduleIndex, 1);
      this.logAuditEvent('schedule_deleted', deletedBy, { scheduleId });
    }
  }

  // Serialization for persistence
  serializeToUpdate(): Uint8Array {
    return Y.encodeStateAsUpdate(this.ydoc);
  }

  // Apply update from storage or sync
  applyUpdate(update: Uint8Array): void {
    Y.applyUpdate(this.ydoc, update);
  }

  // Generate a new UUID
  private generateId(): UUID {
    return crypto.randomUUID();
  }

  // Helper methods for Y.Map conversions
  private memberToYMap(member: Member): Y.Map<any> {
    const yMap = new Y.Map();
    yMap.set('id', member.id);
    yMap.set('role', member.role);
    yMap.set('display', member.display);
    return yMap;
  }

  private yMapToMember(yMap: Y.Map<any>): Member {
    return {
      id: yMap.get('id'),
      role: yMap.get('role'),
      display: yMap.get('display')
    };
  }

  private accountToYMap(account: Account): Y.Map<any> {
    const yMap = new Y.Map();
    Object.entries(account).forEach(([key, value]) => {
      yMap.set(key, value);
    });
    return yMap;
  }

  private yMapToAccount(yMap: Y.Map<any>): Account {
    return {
      id: yMap.get('id'),
      name: yMap.get('name'),
      type: yMap.get('type'),
      currency: yMap.get('currency'),
      openDate: yMap.get('openDate'),
      status: yMap.get('status'),
      balance: yMap.get('balance')
    };
  }

  private categoryToYMap(category: Category): Y.Map<any> {
    const yMap = new Y.Map();
    Object.entries(category).forEach(([key, value]) => {
      if (value !== undefined) {
        yMap.set(key, value);
      }
    });
    return yMap;
  }

  private yMapToCategory(yMap: Y.Map<any>): Category {
    return {
      id: yMap.get('id'),
      name: yMap.get('name'),
      group: yMap.get('group'),
      envelope: yMap.get('envelope'),
      rollover: yMap.get('rollover'),
      target: yMap.get('target'),
      isHidden: yMap.get('isHidden')
    };
  }

  private transactionToYMap(transaction: Transaction): Y.Map<any> {
    const yMap = new Y.Map();
    Object.entries(transaction).forEach(([key, value]) => {
      if (value !== undefined) {
        yMap.set(key, value);
      }
    });
    return yMap;
  }

  private yMapToTransaction(yMap: Y.Map<any>): Transaction {
    return {
      id: yMap.get('id'),
      date: yMap.get('date'),
      accountId: yMap.get('accountId'),
      amount: yMap.get('amount'),
      payee: yMap.get('payee'),
      categoryId: yMap.get('categoryId'),
      memo: yMap.get('memo'),
      tags: yMap.get('tags'),
      splits: yMap.get('splits'),
      receiptIds: yMap.get('receiptIds'),
      cleared: yMap.get('cleared'),
      reconciled: yMap.get('reconciled'),
      createdBy: yMap.get('createdBy'),
      createdAt: yMap.get('createdAt'),
      updatedAt: yMap.get('updatedAt'),
      scheduleId: yMap.get('scheduleId'),
      scheduleDueDate: yMap.get('scheduleDueDate')
    };
  }

  private yMapToBudgetAllocation(yMap: Y.Map<any>): BudgetAllocation {
    return {
      categoryId: yMap.get('categoryId'),
      month: yMap.get('month'),
      allocated: yMap.get('allocated'),
      spent: yMap.get('spent'),
      rollover: yMap.get('rollover')
    };
  }

  private scheduleToYMap(schedule: Schedule): Y.Map<any> {
    const yMap = new Y.Map();
    yMap.set('id', schedule.id);
    yMap.set('type', schedule.type);
    yMap.set('spec', schedule.spec);
    yMap.set('startDate', schedule.startDate);
    yMap.set('endDate', schedule.endDate);
    yMap.set('autoPost', schedule.autoPost);
    yMap.set('nextDue', schedule.nextDue);
    yMap.set('active', schedule.active);
    
    // Handle nested template object
    const templateMap = new Y.Map();
    Object.entries(schedule.template).forEach(([key, value]) => {
      if (value !== undefined) {
        templateMap.set(key, value);
      }
    });
    yMap.set('template', templateMap);
    
    return yMap;
  }

  private yMapToSchedule(yMap: Y.Map<any>): Schedule {
    const templateMap = yMap.get('template');
    const template = {
      accountId: templateMap.get('accountId'),
      categoryId: templateMap.get('categoryId'),
      payee: templateMap.get('payee'),
      memo: templateMap.get('memo'),
      amount: templateMap.get('amount')
    };

    return {
      id: yMap.get('id'),
      type: yMap.get('type'),
      spec: yMap.get('spec'),
      startDate: yMap.get('startDate'),
      endDate: yMap.get('endDate'),
      template,
      autoPost: yMap.get('autoPost'),
      nextDue: yMap.get('nextDue'),
      active: yMap.get('active')
    };
  }

  // Audit logging
  private logAuditEvent(action: string, userId: UUID, details: any): void {
    const auditEntry = new Y.Map();
    auditEntry.set('id', this.generateId());
    auditEntry.set('action', action);
    auditEntry.set('userId', userId);
    auditEntry.set('timestamp', new Date().toISOString());
    auditEntry.set('details', JSON.stringify(details));
    
    this.audit.push([auditEntry]);
    
    // Keep only the last 1000 audit entries
    if (this.audit.length > 1000) {
      this.audit.delete(0, this.audit.length - 1000);
    }
  }
}

export default LedgerDocument;